import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import {
  Wallet, Plus, ArrowDownCircle, ArrowUpCircle,
  RefreshCw, CheckCircle, AlertTriangle, Clock,
  CreditCard, Loader, ChevronRight, Info
} from 'lucide-react';

const TOP_UP_AMOUNTS = [5, 10, 20, 50, 100];

const TX_TYPE_CONFIG = {
  topup_stripe:        { label: 'Card top-up',     color: 'text-green-400',  icon: ArrowUpCircle },
  topup_paypal:        { label: 'PayPal top-up',   color: 'text-green-400',  icon: ArrowUpCircle },
  topup_admin:         { label: 'Admin credit',    color: 'text-blue-400',   icon: ArrowUpCircle },
  deduction_renewal:   { label: 'Auto-renewal',    color: 'text-red-400',    icon: ArrowDownCircle },
  deduction_manual:    { label: 'Manual payment',  color: 'text-red-400',    icon: ArrowDownCircle },
  refund:              { label: 'Refund',           color: 'text-green-400',  icon: ArrowUpCircle },
  adjustment:          { label: 'Adjustment',       color: 'text-yellow-400', icon: RefreshCw },
};

const BalancePage = () => {
  const [searchParams] = useSearchParams();
  const topupStatus = searchParams.get('topup');

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedAmount, setSelectedAmount] = useState(20);
  const [topping, setTopping] = useState(false);
  const [autoRenew, setAutoRenew] = useState({});

  useEffect(() => {
    loadBalance();

    if (topupStatus === 'success') {
      const amount = searchParams.get('amount');
      toast.success(`€${amount} added to your balance!`);
    } else if (topupStatus === 'cancelled') {
      toast('Top-up cancelled', { icon: 'ℹ️' });
    }
  }, []);

  const loadBalance = async () => {
    try {
      const { data: d } = await api.get('/balance');
      setData(d);
      // Set auto-renew state from orders
      const ar = {};
      d.upcoming_renewals?.forEach(r => { ar[r.id] = r.auto_renew !== false; });
      setAutoRenew(ar);
    } catch {
      toast.error('Failed to load balance');
    } finally {
      setLoading(false);
    }
  };

  const handleTopUp = async () => {
    setTopping(true);
    try {
      const { data: d } = await api.post('/balance/topup/stripe', { amount: selectedAmount });
      window.location.href = d.url;
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to start top-up');
      setTopping(false);
    }
  };

  const toggleAutoRenew = async (orderId, value) => {
    setAutoRenew(prev => ({ ...prev, [orderId]: value }));
    try {
      await api.patch(`/billing/orders/${orderId}/auto-renew`, { auto_renew: value });
      toast.success(value ? 'Auto-renew enabled' : 'Auto-renew disabled');
    } catch {
      setAutoRenew(prev => ({ ...prev, [orderId]: !value }));
      toast.error('Failed to update auto-renew');
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center">
      <Loader className="w-8 h-8 text-green-400 animate-spin" />
    </div>
  );

  const balance = data?.balance || 0;
  const isSufficient = data?.balance_sufficient;
  const upcoming = data?.upcoming_renewals || [];
  const transactions = data?.transactions || [];

  return (
    <div className="min-h-screen bg-gray-950 pt-24 pb-16 px-4">
      <div className="max-w-4xl mx-auto">

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-black text-white mb-1">Balance & Credits</h1>
          <p className="text-gray-400">Top up your balance for automatic server renewals</p>
        </div>

        {/* Balance card */}
        <div className={`rounded-2xl p-8 mb-6 border-2 ${
          isSufficient
            ? 'bg-gradient-to-br from-green-500/10 to-gray-900 border-green-500/30'
            : balance > 0
            ? 'bg-gradient-to-br from-yellow-500/10 to-gray-900 border-yellow-500/30'
            : 'bg-gradient-to-br from-gray-800 to-gray-900 border-gray-700'
        }`}>
          <div className="flex items-center justify-between flex-wrap gap-6">
            <div>
              <div className="flex items-center gap-2 text-gray-400 text-sm mb-2">
                <Wallet className="w-4 h-4" />
                Current Balance
              </div>
              <div className={`text-5xl font-black ${
                balance > 0 ? 'text-white' : 'text-gray-500'
              }`}>
                €{parseFloat(balance).toFixed(2)}
              </div>
              {upcoming.length > 0 && (
                <div className={`mt-3 text-sm flex items-center gap-1.5 ${
                  isSufficient ? 'text-green-400' : 'text-yellow-400'
                }`}>
                  {isSufficient
                    ? <><CheckCircle className="w-4 h-4" />Covers all upcoming renewals (€{parseFloat(data.total_upcoming).toFixed(2)} needed)</>
                    : <><AlertTriangle className="w-4 h-4" />Not enough for renewals — €{parseFloat(data.total_upcoming).toFixed(2)} needed</>
                  }
                </div>
              )}
            </div>

            {/* Top-up selector */}
            <div className="bg-gray-900/80 rounded-2xl p-5 border border-gray-700 min-w-64">
              <div className="text-sm font-semibold text-gray-300 mb-3">Add balance</div>
              <div className="grid grid-cols-5 gap-1.5 mb-4">
                {TOP_UP_AMOUNTS.map(amt => (
                  <button
                    key={amt}
                    onClick={() => setSelectedAmount(amt)}
                    className={`py-2 rounded-xl text-sm font-bold transition-colors ${
                      selectedAmount === amt
                        ? 'bg-green-500 text-gray-950'
                        : 'bg-gray-800 hover:bg-gray-700 text-gray-300'
                    }`}
                  >
                    €{amt}
                  </button>
                ))}
              </div>
              <button
                onClick={handleTopUp}
                disabled={topping}
                className="w-full flex items-center justify-center gap-2 bg-green-500 hover:bg-green-400 disabled:opacity-50 text-gray-950 font-bold py-3 rounded-xl transition-colors"
              >
                {topping
                  ? <Loader className="w-4 h-4 animate-spin" />
                  : <><Plus className="w-4 h-4" /> Add €{selectedAmount}</>
                }
              </button>
              <p className="text-gray-600 text-xs text-center mt-2">Secure payment via Stripe</p>
            </div>
          </div>
        </div>

        {/* How it works */}
        <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-5 mb-8 flex gap-3">
          <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-gray-400 leading-relaxed">
            <span className="text-white font-semibold">How auto-renewal works: </span>
            When your server is due for renewal, we automatically deduct from your balance and keep it running. 
            You'll get reminder emails 3 days and 1 day before. If your balance is insufficient, your server will be suspended and you have 14 days to reactivate before it's deleted.
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Upcoming renewals */}
          <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-800 flex items-center justify-between">
              <h2 className="font-bold text-white flex items-center gap-2">
                <Clock className="w-4 h-4 text-yellow-400" />
                Upcoming Renewals
              </h2>
              <span className="text-xs text-gray-500">{upcoming.length} active</span>
            </div>
            {upcoming.length === 0 ? (
              <div className="text-center py-10 text-gray-500 text-sm">No active subscriptions</div>
            ) : (
              <div className="divide-y divide-gray-800">
                {upcoming.map((order) => {
                  const dueDate = new Date(order.next_due_date);
                  const daysUntil = Math.round((dueDate - new Date()) / (1000 * 60 * 60 * 24));
                  const isUrgent = daysUntil <= 3;
                  return (
                    <div key={order.id} className="px-5 py-4">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <div className="font-semibold text-white text-sm">
                            {order.server_name || order.plan_name}
                          </div>
                          <div className="text-gray-500 text-xs mt-0.5">{order.plan_name} · {order.billing_cycle}</div>
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-white">€{parseFloat(order.amount).toFixed(2)}</div>
                          <div className={`text-xs mt-0.5 ${isUrgent ? 'text-yellow-400 font-medium' : 'text-gray-500'}`}>
                            {daysUntil === 0 ? 'Due today' : daysUntil < 0 ? `${Math.abs(daysUntil)}d overdue` : `${daysUntil}d`}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="text-xs text-gray-600">
                          Due {dueDate.toLocaleDateString()}
                        </div>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <span className="text-xs text-gray-500">Auto-renew</span>
                          <div
                            onClick={() => toggleAutoRenew(order.id, !autoRenew[order.id])}
                            className={`w-9 h-5 rounded-full transition-colors relative ${
                              autoRenew[order.id] !== false ? 'bg-green-500' : 'bg-gray-700'
                            }`}
                          >
                            <div className={`w-3.5 h-3.5 bg-white rounded-full absolute top-0.5 transition-all ${
                              autoRenew[order.id] !== false ? 'left-4.5' : 'left-0.5'
                            }`} style={{ left: autoRenew[order.id] !== false ? '1.125rem' : '0.125rem' }} />
                          </div>
                        </label>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Transaction history */}
          <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-800">
              <h2 className="font-bold text-white flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-blue-400" />
                Transaction History
              </h2>
            </div>
            {transactions.length === 0 ? (
              <div className="text-center py-10 text-gray-500 text-sm">No transactions yet</div>
            ) : (
              <div className="divide-y divide-gray-800 max-h-96 overflow-y-auto">
                {transactions.map((tx) => {
                  const cfg = TX_TYPE_CONFIG[tx.type] || TX_TYPE_CONFIG.adjustment;
                  const Icon = cfg.icon;
                  const isPositive = tx.amount > 0;
                  return (
                    <div key={tx.id} className="px-5 py-3.5 flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${
                        isPositive ? 'bg-green-500/10' : 'bg-red-500/10'
                      }`}>
                        <Icon className={`w-4 h-4 ${cfg.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-white font-medium truncate">{cfg.label}</div>
                        {tx.description && (
                          <div className="text-xs text-gray-500 truncate">{tx.description}</div>
                        )}
                        <div className="text-xs text-gray-600 mt-0.5">
                          {new Date(tx.created_at).toLocaleString()}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className={`font-bold text-sm ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
                          {isPositive ? '+' : ''}€{parseFloat(tx.amount).toFixed(2)}
                        </div>
                        <div className="text-gray-600 text-xs">
                          bal: €{parseFloat(tx.balance_after).toFixed(2)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Need more help */}
        <div className="mt-8 text-center">
          <p className="text-gray-500 text-sm">
            Questions about billing?{' '}
            <Link to="/tickets" className="text-green-400 hover:text-green-300 transition-colors">
              Open a support ticket
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default BalancePage;
